import { useMemo } from "react"
import { usePropertyDetailContext, useUserLanguage } from "@/customHooks"
import styled from "styled-components"
import { ButtonWithDropDownMenu } from "../molecules"
import { capitalizeFirstLetter } from "@/helpers"
import { useNavigate } from "react-router"
import { pathURL } from "@/components/constants/urls"
import { ContractStatusOption } from "@/graphql/generated"
import textsData from '@/textData/PropertyDetailTexts.json'
import { ContractStatusPill } from "../atoms"

type TenantUnityRowData = {
  contractId: string;
  contractStatusOption: ContractStatusOption
  unityName: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
};

const URL_TO_NAVIGATE_FOR_CONTRACT_DETAIL = `/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.DETAIL}`

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg} ;
  padding-bottom: ${({ theme }) => theme.spacing.lg} ;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 64px;
  row-gap: 10px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;
  
  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.background2};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
  }
`;

export const PropertyDetailTenantsList = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.tenantsList : textsData.en.tenantsList
  const navigate = useNavigate()

  const { property } = usePropertyDetailContext()
   
  const tableData = useMemo((): TenantUnityRowData[] => {
    if (!property?.unities) {
      return []
    }

    return property.unities.flatMap(unity =>
      unity.contracts
        .map(contract => {
          if (!contract.tenant?.user) return null

          // TODO: 250822 Verificar si es necesario filtrar los tenants segun el status de su contrato
          // if (contract.statusOption !== ContractStatusOption.Initiated) return null
          
          return {
            contractId: contract.id,
            contractStatusOption: contract.statusOption,
            unityName: unity.name,
            tenantId: contract.tenant.user.id,
            firstName: contract.tenant.firstName,
            lastName: contract.tenant.lastName,
            phoneNumber: contract.tenant.phoneNumber,
            email: contract.tenant.user.email,
          };
        })
        .filter((item): item is TenantUnityRowData => item !== null)
    );
  }, [property])

  const onDetailConsultingHandle = (id: string) => {
    navigate(`/${pathURL.USER}/${pathURL.TENANT}/${pathURL.CREATE}`, {
      state: {
        tenantId: id,
        readOnly: true,
      }
    })
  }

  const onContractEditHandler = (contractId: string) => {
    navigate(URL_TO_NAVIGATE_FOR_CONTRACT_DETAIL, { state: { id: contractId}})
  }

  if (tableData.length === 0) {
    return (
      <p><b>{texts.noData}</b></p>
    )
  }
  return (
    <StyledGridTable>
      {/* Encabezado */}
      <StyledGridHeader>
        <div>{texts.tableHeader.unity}</div>
        <div>{texts.tableHeader.name}</div>
        <div>{texts.tableHeader.phone}</div>
        <div>{texts.tableHeader.email}</div>
        <div>{texts.tableHeader.contractStatus}</div>
        <div />
      </StyledGridHeader>

      {/* Filas dinámicas */}
      {tableData.map((data) => (
        <StyledGridRow key={data.contractId}>
          <div>{capitalizeFirstLetter(data.unityName)}</div>
          <div>{`${capitalizeFirstLetter(data.firstName)} ${capitalizeFirstLetter(data.lastName)}`}</div>
          <div>{data.phoneNumber}</div>
          <div>{data.email}</div>
          <div><ContractStatusPill status={data.contractStatusOption} /></div>
          <div>
            <ButtonWithDropDownMenu
              options={[
                {item: texts.dropdownMenu.details, action: () => onDetailConsultingHandle(data.tenantId)},
                {item: texts.dropdownMenu.edit, action: () => {onContractEditHandler(data.contractId)}},
                {item: texts.dropdownMenu.delete, action: () => {}},
              ]}
              position='left'
            />
          </div>
        </StyledGridRow>
      ))}
    </StyledGridTable>
  )
}